import React, { useEffect, useMemo, useRef } from 'react';

import useTitle from 'hooks/useTitle';
import { useFetchAssetsById } from 'hooks/query/useAssetsQuery';
import { handleError } from 'utils/errorHandler';

import Table from 'components/common/table';

import { getFormattedDate } from 'utils/date';

import { DATE_FORMAT_FOR_DISPLAY } from 'constants/appConstants';

interface EmployeeAssetsProps {
  employeeId: number | string;
}

const EmployeeAssets: React.FC<EmployeeAssetsProps> = (props) => {
  const { employeeId: id } = props;

  useTitle('Assets');

  const {
    data,
    isLoading,
    isError: isAssetsError,
    error: assetsError,
    errorUpdatedAt: assetsErrorUpdatedAt,
  } = useFetchAssetsById(id) as {
    data: Array<{ assetTag: string; assetName: string; assetType: string; addedDate: string }>;
    isLoading: boolean;
    isError: boolean;
    error: Error | null;
    errorUpdatedAt: number;
  };
  const lastHandledAssetsErrorAtRef = useRef(0);

  useEffect(() => {
    if (!isAssetsError || !assetsError || assetsErrorUpdatedAt <= lastHandledAssetsErrorAtRef.current) {
      return;
    }

    handleError(assetsError as object);
    lastHandledAssetsErrorAtRef.current = assetsErrorUpdatedAt;
  }, [isAssetsError, assetsError, assetsErrorUpdatedAt]);

  const columns = useMemo(() => {
    return [
      {
        Header: 'Tag',
        accessor: 'assetTag',
      },
      {
        Header: 'Name',
        accessor: 'assetName',
      },
      {
        Header: 'Type',
        accessor: 'assetType',
      },
      {
        Header: 'Added Date',
        accessor: 'addedDate',
        Cell: (row) => <span>{getFormattedDate(row.original.addedDate, DATE_FORMAT_FOR_DISPLAY)}</span>,
      },
    ];
  }, []);

  return (
    <main>
      <div className="container">
        <div className="full-scope-card mt-15">
          <div
            className={[
              'full-scope-card__header',
              'table-header',
              'name-wrap',
              'd-flex',
              'justify-content-between',
              'user-select-none',
            ].join(' ')}
          >
            <div>
              <span className="table-header__title">Assets</span>
            </div>
          </div>
          <Table
            data={data}
            columns={columns}
            loading={isLoading}
            showPagination={false}
            minRows={data?.length}
            customClassName="employees-assets-table rt-cursor-default"
          />
        </div>
      </div>
    </main>
  );
};

export default EmployeeAssets;
